/**
 * 플레어 스택 최소 높이 — KOSHA GUIDE C-C-18-2026(舊 D-59-2020)
 * 「플레어시스템의 설계·설치 및 운전에 관한 기술지침」 Brzustowski-Sommer 법.
 *
 * API 521 Annex F.2 "단순법"(점원 모델, `flareRadiation.ts`)과 달리 이 지침은
 * 제트/바람 추력비(djR)·보정 폭발하한계(CL)로 화염 중심의 수직거리 yc·
 * 수평거리 xc 를 노모그래프(<그림 5>·<그림 6>)에서 구하도록 명시한다.
 * 사용자가 제공한 실제 KOSHA 기준 계산 엑셀(HyREX DR+ESF Flare Stack
 * 프로젝트)의 수식을 그대로 따른다:
 *
 *   1) dj = √((1/Mach)·3.23×10⁻⁵·(W/Pj)·√(Z·Tj/Mj))         — 스택(팁) 지름
 *   2) Uj = Mach·91.2·√(Tj/Mj)                                — 팁 방출속도
 *   3) CL = CL'·(Uj/Uw)·(Mj/Ma)                                — 보정 폭발하한계
 *   4) djR = dj·(Uj/Uw)·√(Ta·Mj/Tj)                            — 제트/바람 추력비
 *   5) yc = f(CL, djR), xc = f(CL, djR)                        — <그림 5>·<그림 6>
 *      노모그래프를 디지털화한 곡선(`src/data/koshaFlareBs.ts`)에서 조회
 *   6) D = √(τ·F·Q / (4π·(K−K0)))                              — Hajek-Ludwig 식
 *   7) H = D − yc                                              — 최소 스택 높이
 *
 * 단위는 실제 계산 엑셀·KOSHA 지침 관례를 그대로 따른다: W(kg/hr), Pj(kPa
 * 절대압), Tj·Ta(K), Q(kcal/hr), K·K0(kcal/hr·m²) — API 521 쪽(kW 등)과
 * 단위계가 다르므로 섞어 쓰지 않는다.
 *
 * ⚠ yc·xc 조회 테이블은 그래프를 픽셀 좌표로 디지털화한 근사치다 —
 *   `data/kosha-flare-bs.json` 의 accuracyNote 참고. 특히 djR 75~150
 *   구간은 검증 오차가 최대 16%대로 다른 구간보다 크다.
 *
 * 검증: `data/verify-kosha-flare-bs.py` 가 실제 프로젝트 파일에서 가져온
 * 5개 사례(CL·djR→yc·xc, 엔지니어가 차트를 직접 읽어 확정한 값)로 대조한다.
 */
import { XC_TABLE, YC_TABLE, type BsCurveTable, type BsCurvePoint } from "../data/koshaFlareBs";

/** 1) 스택(팁) 내경 dj[m]. Pj 는 절대압(kPa), W 는 kg/hr. */
export function jetDiameterM(
  machNumber: number,
  massFlowKgH: number,
  tipPressureKpaAbs: number,
  compressibility: number,
  tipTempK: number,
  molecularWeightKgKmol: number,
): number {
  return Math.sqrt(
    (1 / machNumber) *
      3.23e-5 *
      (massFlowKgH / tipPressureKpaAbs) *
      Math.sqrt((compressibility * tipTempK) / molecularWeightKgKmol),
  );
}

/** 2) 플레어 팁 방출속도 Uj[m/s]. */
export function jetVelocityMs(machNumber: number, tipTempK: number, molecularWeightKgKmol: number): number {
  return machNumber * 91.2 * Math.sqrt(tipTempK / molecularWeightKgKmol);
}

/** 3) 보정 폭발하한계 CL(무차원). CL' 은 <표 1> 등에서 구하는 유체 고유값. */
export function correctedLel(
  lelPrime: number,
  jetVelocityMsIn: number,
  windVelocityMs: number,
  molecularWeightKgKmol: number,
  airMolecularWeightKgKmol: number,
): number {
  return lelPrime * (jetVelocityMsIn / windVelocityMs) * (molecularWeightKgKmol / airMolecularWeightKgKmol);
}

/** 4) 제트추력/바람추력 계수 djR(무차원). */
export function jetToWindThrustRatio(
  jetDiameterMIn: number,
  jetVelocityMsIn: number,
  windVelocityMs: number,
  ambientTempK: number,
  molecularWeightKgKmol: number,
  tipTempK: number,
): number {
  return (
    jetDiameterMIn *
    (jetVelocityMsIn / windVelocityMs) *
    Math.sqrt((ambientTempK * molecularWeightKgKmol) / tipTempK)
  );
}

export interface BsLookupResult {
  readonly value: number;
  /** djR 가 표 범위(djR 최소~최대)를 벗어나 표 끝 선으로 외삽했는지. */
  readonly djRExtrapolated: boolean;
}

/** 두 점을 잇는 거듭제곱식(v = K·CL^n)으로 그 구간의 지수를 구한다. */
function segmentExponent(cl1: number, v1: number, cl2: number, v2: number): number {
  return (Math.log(v2) - Math.log(v1)) / (Math.log(cl2) - Math.log(cl1));
}

function evalPowerLaw(clRef: number, vRef: number, exponent: number, cl: number): number {
  const k = vRef / Math.pow(clRef, exponent);
  return k * Math.pow(cl, exponent);
}

/**
 * djR 선 하나를 원하는 CL 까지 구한다. 실측점(samples)이 1개뿐이면 그
 * 대표점과 (선 고유 또는 테이블 공통) 거듭제곱 지수로 만든 단일 식을 쓰고,
 * 2개 이상이면 인접 샘플 사이를 조각별 거듭제곱으로 이어 그 구간 안에서는
 * 그 구간 자체의 기울기를, 범위 밖이면 가장 가까운 구간의 기울기로
 * 외삽한다 — 사람이 그래프 위 실측점들 사이를 선으로 잇듯이 읽는 것과 같다.
 */
function evalPointAtCl(point: BsCurvePoint, tableExponent: number, cl: number): number {
  const { samples } = point;
  if (samples.length === 1) {
    const only = samples[0]!;
    return evalPowerLaw(only.cl, only.v, point.exponent ?? tableExponent, cl);
  }
  let i1 = samples.findIndex((s) => s.cl >= cl);
  if (i1 <= 0) i1 = 1;
  if (i1 >= samples.length) i1 = samples.length - 1;
  const lo = samples[i1 - 1]!;
  const hi = samples[i1]!;
  const n = segmentExponent(lo.cl, lo.v, hi.cl, hi.v);
  return evalPowerLaw(lo.cl, lo.v, n, cl);
}

/**
 * 5) <그림 5>(yc) 또는 <그림 6>(xc) 노모그래프 조회 — 이웃한 두 djR 선을
 * 각각 그 선 고유의(없으면 차트 공통) CL 방향 거듭제곱 지수로 원하는 CL
 * 까지 외삽한 뒤, 그 두 값을 log(djR) 축으로 보간한다 — 사람이 그래프를
 * 읽을 때 "이 djR 선을 따라 CL 위치까지 미끄러진 뒤, 위아래 이웃 선 사이를
 * 눈대중으로 보간"하는 절차와 같다. 선 고유 지수는 그 선 위에서 실측한
 * 2점(사용자가 직접 그래프를 읽어 준 값 포함)에서 구한 것이라, 차트
 * 공통값보다 그 선 자체의 국소 기울기를 더 정확히 반영한다.
 */
export function lookupBsCurve(table: BsCurveTable, djR: number, cl: number): BsLookupResult {
  const { exponent, points } = table; // points 는 이미 djR 오름차순
  const djRMin = points[0]!.djR;
  const djRMax = points[points.length - 1]!.djR;
  const djRExtrapolated = djR < djRMin || djR > djRMax;
  const djRClamped = Math.min(djRMax, Math.max(djRMin, djR));

  let i1 = points.findIndex((p) => p.djR >= djRClamped);
  if (i1 <= 0) i1 = 1;
  const i0 = i1 - 1;
  const lo = points[i0]!;
  const hi = points[i1]!;

  const vLo = evalPointAtCl(lo, exponent, cl);
  const vHi = evalPointAtCl(hi, exponent, cl);

  const t = (Math.log(djRClamped) - Math.log(lo.djR)) / (Math.log(hi.djR) - Math.log(lo.djR));
  const value = Math.exp(Math.log(vLo) + t * (Math.log(vHi) - Math.log(vLo)));

  return { value, djRExtrapolated };
}

/** 6) 화염 중심~대상지점 거리 D[m] — Hajek-Ludwig 식(배경/태양복사 K0 차감 포함). */
export function radiationDistanceM(
  transmissivity: number,
  fractionHeatRadiated: number,
  heatReleaseKcalH: number,
  allowableRadiationKcalHm2: number,
  backgroundRadiationKcalHm2: number,
): number {
  const netK = allowableRadiationKcalHm2 - backgroundRadiationKcalHm2;
  return Math.sqrt((transmissivity * fractionHeatRadiated * heatReleaseKcalH) / (4 * Math.PI * netK));
}

export interface KoshaFlareInput {
  readonly lelPrime: number;
  readonly molecularWeightKgKmol: number;
  readonly massFlowKgH: number;
  readonly heatingValueKcalKg: number;
  readonly tipPressureKpaAbs: number;
  readonly tipTempK: number;
  readonly ambientTempK: number;
  readonly windVelocityMs: number;
  readonly airMolecularWeightKgKmol: number;
  readonly compressibility: number;
  readonly machNumber: number;
  readonly transmissivity: number;
  readonly allowableRadiationKcalHm2: number;
  readonly backgroundRadiationKcalHm2: number;
  readonly fractionHeatRadiated: number;
}

export interface KoshaFlareSuccess {
  readonly ok: true;
  readonly jetDiameterM: number;
  readonly jetVelocityMs: number;
  readonly correctedLel: number;
  readonly jetToWindThrustRatio: number;
  readonly heatReleaseKcalH: number;
  readonly ycM: number;
  readonly xcM: number;
  readonly radiationDistanceM: number;
  readonly requiredHeightM: number;
  readonly warnings: readonly string[];
}

export interface KoshaFlareFailure {
  readonly ok: false;
  readonly reason: "invalid-input";
  readonly message: string;
}

export type KoshaFlareResult = KoshaFlareSuccess | KoshaFlareFailure;

function invalid(message: string): KoshaFlareFailure {
  return { ok: false, reason: "invalid-input", message };
}

export function requiredFlareHeightKosha(input: KoshaFlareInput): KoshaFlareResult {
  if (!Number.isFinite(input.lelPrime) || input.lelPrime <= 0) {
    return invalid("폭발하한계 CL' 은 0보다 커야 합니다.");
  }
  if (!Number.isFinite(input.molecularWeightKgKmol) || input.molecularWeightKgKmol <= 0) {
    return invalid("분자량은 0보다 커야 합니다.");
  }
  if (!Number.isFinite(input.massFlowKgH) || input.massFlowKgH <= 0) {
    return invalid("질량유량은 0보다 커야 합니다.");
  }
  if (!Number.isFinite(input.heatingValueKcalKg) || input.heatingValueKcalKg <= 0) {
    return invalid("연소열량(HHV)은 0보다 커야 합니다.");
  }
  if (!Number.isFinite(input.tipPressureKpaAbs) || input.tipPressureKpaAbs <= 0) {
    return invalid("플레어가스 압력(절대압)은 0보다 커야 합니다.");
  }
  if (!Number.isFinite(input.tipTempK) || input.tipTempK <= 0) {
    return invalid("플레어가스 온도는 0 K보다 커야 합니다.");
  }
  if (!Number.isFinite(input.ambientTempK) || input.ambientTempK <= 0) {
    return invalid("대기온도는 0 K보다 커야 합니다.");
  }
  if (!Number.isFinite(input.windVelocityMs) || input.windVelocityMs <= 0) {
    return invalid("풍속은 0보다 커야 합니다.");
  }
  if (!Number.isFinite(input.airMolecularWeightKgKmol) || input.airMolecularWeightKgKmol <= 0) {
    return invalid("공기 분자량은 0보다 커야 합니다.");
  }
  if (!Number.isFinite(input.compressibility) || input.compressibility <= 0) {
    return invalid("압축인자 Z 는 0보다 커야 합니다.");
  }
  if (!Number.isFinite(input.machNumber) || input.machNumber <= 0) {
    return invalid("마하수는 0보다 커야 합니다.");
  }
  if (input.machNumber < 0.2 || input.machNumber > 0.5) {
    return invalid("마하수는 KOSHA 지침 적용범위인 0.2~0.5 사이여야 합니다.");
  }
  if (!Number.isFinite(input.transmissivity) || input.transmissivity <= 0 || input.transmissivity > 1) {
    return invalid("대기 투과율 τ 는 0보다 크고 1 이하여야 합니다.");
  }
  if (!Number.isFinite(input.fractionHeatRadiated) || input.fractionHeatRadiated <= 0 || input.fractionHeatRadiated > 1) {
    return invalid("방출 복사열 비율 F 는 0보다 크고 1 이하여야 합니다.");
  }
  if (!Number.isFinite(input.backgroundRadiationKcalHm2) || input.backgroundRadiationKcalHm2 < 0) {
    return invalid("배경(태양) 복사량 K0 는 0 이상이어야 합니다.");
  }
  if (
    !Number.isFinite(input.allowableRadiationKcalHm2) ||
    input.allowableRadiationKcalHm2 <= input.backgroundRadiationKcalHm2
  ) {
    return invalid("최대허용 복사열량 K 는 배경(태양) 복사량 K0 보다 커야 합니다.");
  }

  const warnings: string[] = [];

  const dj = jetDiameterM(
    input.machNumber,
    input.massFlowKgH,
    input.tipPressureKpaAbs,
    input.compressibility,
    input.tipTempK,
    input.molecularWeightKgKmol,
  );
  const uj = jetVelocityMs(input.machNumber, input.tipTempK, input.molecularWeightKgKmol);
  const cl = correctedLel(input.lelPrime, uj, input.windVelocityMs, input.molecularWeightKgKmol, input.airMolecularWeightKgKmol);
  const djR = jetToWindThrustRatio(dj, uj, input.windVelocityMs, input.ambientTempK, input.molecularWeightKgKmol, input.tipTempK);

  const ycLookup = lookupBsCurve(YC_TABLE, djR, cl);
  const xcLookup = lookupBsCurve(XC_TABLE, djR, cl);
  if (ycLookup.djRExtrapolated || xcLookup.djRExtrapolated) {
    warnings.push(
      `djR(${djR.toFixed(2)})가 <그림 5>·<그림 6> 노모그래프의 확보된 범위(djR 3~1200 / 0.3~1200)를 벗어나 끝 선으로 외삽했습니다 — 결과를 신중히 검토하십시오.`,
    );
  }
  warnings.push(
    "yc·xc 는 KOSHA <그림 5>·<그림 6> 노모그래프를 디지털화한 근사표에서 구한 값입니다(데이터 출처·오차 범위는 data/kosha-flare-bs.json 참고). djR 75~150 구간은 검증 오차가 더 큽니다 — 설계 여유가 크지 않다면 원본 그림으로 반드시 재확인하십시오.",
  );

  const heatReleaseKcalH = input.heatingValueKcalKg * input.massFlowKgH;
  const dGeom = radiationDistanceM(
    input.transmissivity,
    input.fractionHeatRadiated,
    heatReleaseKcalH,
    input.allowableRadiationKcalHm2,
    input.backgroundRadiationKcalHm2,
  );

  const requiredHeightM = dGeom - ycLookup.value;
  if (requiredHeightM < 0) {
    warnings.push("계산된 D 가 yc 보다 작아 필요 높이가 음수로 나왔습니다 — 입력값(특히 허용 복사강도 K)을 확인하십시오.");
  }

  return {
    ok: true,
    jetDiameterM: dj,
    jetVelocityMs: uj,
    correctedLel: cl,
    jetToWindThrustRatio: djR,
    heatReleaseKcalH,
    ycM: ycLookup.value,
    xcM: xcLookup.value,
    radiationDistanceM: dGeom,
    requiredHeightM,
    warnings,
  };
}
